import { Box, Typography } from "@fullscript/aviary-web";
import {
  faVial,
  faChartLine,
  faUserDoctor,
  faFileLines,
  faPrescriptionBottle,
  faPersonWalking,
} from "@fullscript/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { Spacer, Arrange, Cell } from "@aviary";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { LabsJourneyPackage } from "@shared/types/graphqlGenerated";
import { insertSoftHyphens } from "@shared/utils/insertSoftHyphens/insertSoftHyphens";
import { usePublicJourneyPackageUnauthenticatedSignUpSignInQuery } from "@unauthenticated/signupSignin/data/queries/PublicJourneyPackageQuery/PublicJourneyPackage.query.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneyOrderSummary.styles";

type LabJourneyPackage = Pick<
  LabsJourneyPackage,
  | "id"
  | "totalPrice"
  | "name"
  | "practitionerName"
  | "displayName"
  | "biomarkerCount"
  | "description"
  | "discount"
>;

interface OrderItem {
  text: string;
  icon: any;
}

interface Props {
  journeyPackageId: string;
}

const JourneyOrderSummary = ({ journeyPackageId }: Props) => {
  const { t } = useTranslation();

  const { data: journeyPackageData, loading: journeyPackageLoading } =
    usePublicJourneyPackageUnauthenticatedSignUpSignInQuery({
      variables: {
        id: journeyPackageId,
      },
    });

  const journeyPackage = journeyPackageData?.viewer?.journeyPackage as
    | LabJourneyPackage
    | undefined;
  const practitionerName = journeyPackage?.displayName || journeyPackage?.practitionerName;
  const journeyPackageName = journeyPackage?.name;
  const biomarkerCount = journeyPackage?.biomarkerCount || 0;
  const storeDiscount = journeyPackage?.discount || 0;

  const displayProviderName = t(l.journeyCheckout.orderSummary.journeyWith, {
    practitionerName,
    interpolation: { escapeValue: false },
  });

  const displayPackageName = journeyPackageName;
  const displayPackageNameWithHyphens = displayPackageName
    ? insertSoftHyphens(displayPackageName)
    : "";

  const interpretationItems: OrderItem[] = [
    {
      text: t(l.journeyCheckout.orderSummary.features.labMarkers, { count: biomarkerCount }),
      icon: faVial,
    },
    { text: t(l.journeyCheckout.orderSummary.features.chartAccess), icon: faChartLine },
    {
      text: t(l.journeyCheckout.orderSummary.features.detailedInterpretation),
      icon: faFileLines,
    },
    storeDiscount > 0 && {
      text: t(l.journeyCheckout.orderSummary.features.supplementList, { storeDiscount }),
      icon: faPrescriptionBottle,
    },
    {
      text: t(l.journeyCheckout.orderSummary.features.lifestyleRecommendations),
      icon: faPersonWalking,
    },
    {
      text: t(l.journeyCheckout.orderSummary.features.nextSteps),
      icon: faUserDoctor,
    },
  ].filter(i => !!i);

  const renderHeader = () => (
    <div>
      <Cell isFullWidth css={styles.centerText}>
        <Typography isLoading={!displayProviderName}>{displayProviderName}</Typography>
      </Cell>
      <Spacer height="quarter" />
      <Typography variant="h3" isLoading={!displayPackageName}>
        {displayPackageNameWithHyphens}
      </Typography>
      <Spacer height="half" />
    </div>
  );

  const renderInterpretationSection = () => (
    <div css={styles.interpretationSection}>
      <Typography variant="h5">{t(l.journeyCheckout.orderSummary.interpretationTitle)}</Typography>
      <Spacer height="quarter" />
      <Arrange spacing="xsmall" isVertical>
        {interpretationItems.map(renderOrderItem)}
      </Arrange>
    </div>
  );

  const renderOrderItem = (item: OrderItem, index: number) => (
    <Arrange key={index} css={styles.orderItem}>
      <FontAwesomeIcon icon={item.icon} css={styles.itemIcon} />
      <Typography>{item.text}</Typography>
    </Arrange>
  );

  const renderSubtotal = () => (
    <div css={styles.subtotalSection}>
      <Typography>{t(l.journeyCheckout.orderSummary.subtotal)}</Typography>
      <Typography>
        $
        {(
          journeyPackageData?.viewer?.journeyPackage as LabJourneyPackage | undefined
        )?.totalPrice?.toFixed(2)}
      </Typography>
    </div>
  );

  const renderTaxes = () => (
    <div css={styles.subtotalSection}>
      <Typography>{t(l.journeyCheckout.orderSummary.taxesEstimated)}</Typography>
      <Typography>{t(l.journeyCheckout.orderSummary.taxesDeferred)}</Typography>
    </div>
  );

  const renderTotal = () => (
    <div css={styles.subtotalSection}>
      <Typography as="p" variant="h3">
        {t(l.journeyCheckout.orderSummary.total)}
      </Typography>
      <Typography as="p" variant="h3">
        $
        {(
          journeyPackageData?.viewer?.journeyPackage as LabJourneyPackage | undefined
        )?.totalPrice?.toFixed(2)}
      </Typography>
    </div>
  );

  if (journeyPackageLoading) {
    return null;
  }

  return (
    <Box padding="small">
      <Typography variant="h3">{t(l.journeyCheckout.orderSummary.title)}</Typography>
      <Spacer height="half" />
      <Box intention="alternate" padding="small" customCss={styles.summaryContainer}>
        {renderHeader()}
        {renderInterpretationSection()}
      </Box>
      <Spacer height="half" />
      {renderSubtotal()}
      {renderTaxes()}
      {renderTotal()}
    </Box>
  );
};

export { JourneyOrderSummary };
